/**
 * TrackingContext - explicit GPS tracking session lifecycle.
 * Buffers GPS pings to localStorage while offline and flushes on reconnect.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";

const TrackingContext = createContext(null);

const OFFLINE_QUEUE_KEY = "hscis_gps_queue";
const ACTIVE_SESSION_KEY = "hscis_active_tracking_session";
const PING_INTERVAL_MS = 30_000;
const MAX_ACCURACY_M = 100;

function readQueue() {
  try {
    return JSON.parse(localStorage.getItem(OFFLINE_QUEUE_KEY) || "[]");
  } catch {
    return [];
  }
}

function saveQueue(queue) {
  localStorage.setItem(OFFLINE_QUEUE_KEY, JSON.stringify(queue));
}

function readStoredSession() {
  try {
    return JSON.parse(localStorage.getItem(ACTIVE_SESSION_KEY) || "null");
  } catch {
    return null;
  }
}

function storeSession(session) {
  if (session) {
    localStorage.setItem(ACTIVE_SESSION_KEY, JSON.stringify(session));
  } else {
    localStorage.removeItem(ACTIVE_SESSION_KEY);
  }
}

function haversineKm(a, b) {
  const radiusKm = 6371.0088;
  const toRad = (value) => (value * Math.PI) / 180;
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const dLat = lat2 - lat1;
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * radiusKm * Math.asin(Math.sqrt(h));
}

function formatGeoError(err) {
  if (err?.code === 1) return "Izin lokasi ditolak. Aktifkan GPS untuk mulai tracking.";
  if (err?.code === 2) return "Lokasi belum tersedia. Coba lagi di area dengan sinyal GPS lebih baik.";
  if (err?.code === 3) return "Permintaan GPS timeout. Coba mulai tracking lagi.";
  return err?.message || "Gagal membaca lokasi GPS.";
}

function requestCurrentPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Browser tidak mendukung GPS."));
      return;
    }
    navigator.geolocation.getCurrentPosition(resolve, reject, {
      enableHighAccuracy: true,
      timeout: 15_000,
      maximumAge: 10_000,
    });
  });
}

function toRoutePoint(payload) {
  return {
    lat: payload.lat,
    lng: payload.lng,
    accuracy: payload.accuracy,
    ts: payload.ts,
  };
}

function isAccuratePoint(point) {
  return point.accuracy == null || point.accuracy <= MAX_ACCURACY_M;
}

export const TrackingProvider = ({ children }) => {
  const { user } = useAuth();
  const [currentPos, setCurrentPos] = useState(null);
  const [tracking, setTracking] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionRoute, setSessionRoute] = useState([]);
  const [lastError, setLastError] = useState(null);
  const [starting, setStarting] = useState(false);
  const [clockTick, setClockTick] = useState(0);
  const watchIdRef = useRef(null);
  const intervalRef = useRef(null);
  const activeSessionRef = useRef(null);
  const poorAccuracyNotifiedRef = useRef(false);

  const isSales = user?.role === "sales";

  const stopWatch = useCallback(() => {
    if (watchIdRef.current !== null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setTracking(false);
  }, []);

  const flushQueue = useCallback(async () => {
    const queue = readQueue();
    if (!queue.length) return;
    try {
      await api.post("/tracking/ping/batch", queue);
      saveQueue([]);
    } catch {
      // Keep queue for the next online attempt.
    }
  }, []);

  const appendRoutePoint = useCallback((payload) => {
    const point = toRoutePoint(payload);
    setSessionRoute((route) => {
      const last = route[route.length - 1];
      if (last?.ts === point.ts || (last?.lat === point.lat && last?.lng === point.lng)) {
        return route;
      }
      return [...route, point];
    });
  }, []);

  const updateCurrentPosition = useCallback((payload) => {
    setCurrentPos(toRoutePoint(payload));
  }, []);

  const sendPing = useCallback(
    async (pos) => {
      const session = activeSessionRef.current;
      if (!session?.id) return;

      const accuracy = pos.coords.accuracy;
      const lowAccuracy = accuracy && accuracy > MAX_ACCURACY_M;
      if (lowAccuracy && !poorAccuracyNotifiedRef.current) {
        poorAccuracyNotifiedRef.current = true;
        toast.warning(
          "Akurasi GPS masih rendah. Titik tetap dicatat, tetapi akan ditandai untuk validasi."
        );
      }

      const payload = {
        session_id: session.id,
        lat: pos.coords.latitude,
        lng: pos.coords.longitude,
        accuracy,
        speed: pos.coords.speed ?? null,
        heading: pos.coords.heading ?? null,
        ts: new Date().toISOString(),
      };

      updateCurrentPosition(payload);

      try {
        const { data } = await api.post("/tracking/ping", payload);
        if (!data.suspicious) {
          appendRoutePoint(data);
        }
      } catch {
        const queue = readQueue();
        queue.push(payload);
        saveQueue(queue);
        if (!lowAccuracy) {
          appendRoutePoint(payload);
        }
      }
    },
    [appendRoutePoint, updateCurrentPosition]
  );

  const startWatch = useCallback(() => {
    if (!navigator.geolocation) {
      toast.error("Browser tidak mendukung GPS.");
      return false;
    }
    stopWatch();
    setTracking(true);
    setLastError(null);

    watchIdRef.current = navigator.geolocation.watchPosition(
      sendPing,
      (err) => {
        const message = formatGeoError(err);
        setLastError(message);
        toast.error(message);
      },
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 10_000 }
    );

    intervalRef.current = setInterval(() => {
      navigator.geolocation.getCurrentPosition(sendPing, () => {}, {
        enableHighAccuracy: true,
        timeout: 10_000,
        maximumAge: 20_000,
      });
    }, PING_INTERVAL_MS);
    return true;
  }, [sendPing, stopWatch]);

  const loadHistory = useCallback(async (sessionId) => {
    if (!sessionId) return;
    try {
      const { data } = await api.get("/tracking/history", { params: { session_id: sessionId } });
      const points = data
        .filter((p) => !p.suspicious)
        .map((p) => ({ lat: p.lat, lng: p.lng, accuracy: p.accuracy, ts: p.ts }));
      setSessionRoute(points);
      setCurrentPos(points[points.length - 1] || null);
    } catch {
      // History is helpful for rendering, but tracking can continue without it.
    }
  }, []);

  const startTracking = useCallback(async () => {
    if (!isSales) return null;
    setStarting(true);
    try {
      const initialPosition = await requestCurrentPosition();
      poorAccuracyNotifiedRef.current = false;

      const { data } = await api.post("/tracking/start");
      setActiveSession(data);
      activeSessionRef.current = data;
      storeSession(data);
      await loadHistory(data.id);
      startWatch();
      await sendPing(initialPosition);
      await flushQueue();
      toast.success("Tracking dimulai.");
      return data;
    } catch (err) {
      const message = err?.response?.data?.detail || formatGeoError(err) || "Gagal memulai tracking.";
      setLastError(message);
      toast.error(message);
      return null;
    } finally {
      setStarting(false);
    }
  }, [flushQueue, isSales, loadHistory, sendPing, startWatch]);

  const stopTracking = useCallback(async () => {
    const session = activeSessionRef.current;
    stopWatch();
    activeSessionRef.current = null;
    setActiveSession((current) =>
      current
        ? {
            ...current,
            end_time: new Date().toISOString(),
            status: "completed",
          }
        : null
    );
    storeSession(null);

    if (!session?.id) {
      return null;
    }

    try {
      await flushQueue();
      const { data } = await api.post("/tracking/stop", { session_id: session.id });
      setActiveSession(data);
      toast.success("Tracking dihentikan.");
      return data;
    } catch (err) {
      const isMissingSession = [404, 409].includes(err?.response?.status);
      const message = isMissingSession
        ? "Tracking lokal dihentikan. Sesi backend sebelumnya tidak ditemukan."
        : "Tracking lokal dihentikan. Ringkasan backend akan disinkronkan saat koneksi stabil.";
      setLastError(message);
      toast.warning(message);
      return null;
    }
  }, [flushQueue, stopWatch]);

  useEffect(() => {
    activeSessionRef.current = activeSession;
  }, [activeSession]);

  useEffect(() => {
    if (!isSales) {
      stopWatch();
      setActiveSession(null);
      setSessionRoute([]);
      setCurrentPos(null);
      activeSessionRef.current = null;
      return;
    }

    const stored = readStoredSession();
    if (stored?.id && stored.status === "active") {
      setActiveSession(stored);
      activeSessionRef.current = stored;
      loadHistory(stored.id);
      startWatch();
    }

    window.addEventListener("online", flushQueue);
    return () => {
      window.removeEventListener("online", flushQueue);
      stopWatch();
    };
  }, [flushQueue, isSales, loadHistory, startWatch, stopWatch]);

  useEffect(() => {
    if (!tracking) return undefined;
    const id = setInterval(() => setClockTick((tick) => tick + 1), 1000);
    return () => clearInterval(id);
  }, [tracking]);

  const distanceKm = useMemo(() => {
    const validRoute = sessionRoute.filter(isAccuratePoint);
    return validRoute.reduce((total, point, index) => {
      if (index === 0) return total;
      return total + haversineKm(validRoute[index - 1], point);
    }, 0);
  }, [sessionRoute]);

  const durationSec = (() => {
    if (!activeSession?.end_time) {
      void clockTick;
    }
    if (!activeSession?.start_time) return 0;
    const end = activeSession.end_time ? new Date(activeSession.end_time) : new Date();
    return Math.max(0, Math.floor((end - new Date(activeSession.start_time)) / 1000));
  })();

  return (
    <TrackingContext.Provider
      value={{
        activeSession,
        currentPos,
        distanceKm,
        durationSec,
        lastError,
        sessionRoute,
        startTracking,
        starting,
        stopTracking,
        tracking,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
};

export const useTracking = () => {
  const ctx = useContext(TrackingContext);
  if (!ctx) throw new Error("useTracking must be inside TrackingProvider");
  return ctx;
};
